/**
 * OPFSBlobCache — streaming file cache via Origin Private File System.
 *
 * Used by the tus upload pipeline to store large files locally without:
 * - Loading the entire file into RAM (streaming via FileSystemWritableFileStream)
 * - IndexedDB quota limits (~500MB per origin)
 * - BlobEngine/nbstore sync pipeline (which buffers everything in Uint8Array)
 *
 * OPFS provides a sandboxed filesystem with practically unlimited storage.
 * Files persist across page reloads and browser restarts.
 */

const OPFS_ROOT = 'sl-opfs-blob-cache';
const METADATA_SUFFIX = '.meta.json';
const DEFAULT_RANGE_CHUNK_SIZE = 4 * 1024 * 1024;

export type OPFSBlobCacheSource = 'upload' | 'download' | 'range';
export type OPFSBlobUploadState = 'pending' | 'synced';

export interface OPFSBlobCacheRange {
  start: number;
  end: number;
}

export interface OPFSBlobCacheInfo {
  key: string;
  mime: string;
  size: number;
  createdAt: string;
  source: OPFSBlobCacheSource;
  complete?: boolean;
  ranges?: OPFSBlobCacheRange[];
  cacheToken?: string;
  uploadState?: OPFSBlobUploadState;
}

export interface OPFSBlobCacheSetOptions {
  mime?: string;
  size?: number;
  source?: OPFSBlobCacheSource;
  cacheToken?: string;
  uploadState?: OPFSBlobUploadState;
}

export interface OPFSBlobCacheRangeOptions extends OPFSBlobCacheSetOptions {
  totalSize?: number;
  expectedLength?: number;
}

export interface OPFSBlobCacheRemoteOptions extends OPFSBlobCacheSetOptions {
  chunkSize?: number;
  fetchRange: (start: number, endInclusive: number) => Promise<Response | null>;
}

export interface OPFSBlobCacheCompleteOptions {
  size?: number;
  cacheToken?: string;
}

export interface OPFSBlobCacheDiagnostics {
  hits: number;
  misses: number;
  fullWrites: number;
  rangeWrites: number;
  rangeRequests: number;
  rangeBytes: number;
  remoteCompletions: number;
  errors: number;
}

export type OPFSBlobCacheUnavailableReason =
  | 'api-missing'
  | 'security-error'
  | 'unknown-error'
  | 'opfs-error';

export type OPFSBlobCacheAvailability =
  | { available: true }
  | {
      available: false;
      reason: OPFSBlobCacheUnavailableReason;
      errorName?: string;
      message?: string;
    };

const diagnostics: OPFSBlobCacheDiagnostics = {
  hits: 0,
  misses: 0,
  fullWrites: 0,
  rangeWrites: 0,
  rangeRequests: 0,
  rangeBytes: 0,
  remoteCompletions: 0,
  errors: 0,
};

function bump(metric: keyof OPFSBlobCacheDiagnostics, by = 1): void {
  diagnostics[metric] += by;
}

/** Check once if OPFS is available in this browser context */
function isOPFSAvailable(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.storage?.getDirectory === 'function'
  );
}

function initialAvailability(): OPFSBlobCacheAvailability {
  return isOPFSAvailable()
    ? { available: true }
    : {
        available: false,
        reason: 'api-missing',
        message: 'navigator.storage.getDirectory is not available',
      };
}

function unavailableFromError(error: unknown): OPFSBlobCacheAvailability {
  const isDOMException =
    typeof DOMException !== 'undefined' && error instanceof DOMException;
  const isError = error instanceof Error;
  const errorName = isDOMException || isError ? error.name : undefined;
  const message = isDOMException || isError ? error.message : undefined;

  if (errorName === 'SecurityError') {
    return { available: false, reason: 'security-error', errorName, message };
  }
  if (errorName === 'UnknownError') {
    return { available: false, reason: 'unknown-error', errorName, message };
  }
  return { available: false, reason: 'opfs-error', errorName, message };
}

let rootPromise: Promise<FileSystemDirectoryHandle> | null = null;
let opfsAvailability = initialAvailability();
let opfsAvailable = opfsAvailability.available;

function disableOPFS(error?: unknown): void {
  opfsAvailable = false;
  opfsAvailability = error
    ? unavailableFromError(error)
    : {
        available: false,
        reason: 'opfs-error',
      };
  rootPromise = null;
}

async function getRoot(): Promise<FileSystemDirectoryHandle> {
  if (!opfsAvailable || !navigator.storage?.getDirectory) {
    throw new Error('OPFS is not available');
  }

  if (!rootPromise) {
    rootPromise = navigator.storage
      .getDirectory()
      .then(root => root.getDirectoryHandle(OPFS_ROOT, { create: true }))
      .catch(err => {
        disableOPFS(err);
        throw err;
      });
  }
  return rootPromise;
}

async function getWorkspaceDir(
  workspaceId: string
): Promise<FileSystemDirectoryHandle> {
  const root = await getRoot();
  return root.getDirectoryHandle(workspaceId, { create: true });
}

/**
 * Encode a blob key for use as an OPFS filename.
 * Base64url keys may contain characters that are fine in OPFS,
 * but we encode `=` and `/` to be safe across implementations.
 */
function encodeKey(key: string): string {
  return key.replace(/=/g, '_eq').replace(/\//g, '_sl');
}

function isReadableByteStream(
  source: File | Blob | ReadableStream<Uint8Array>
): source is ReadableStream<Uint8Array> {
  return (
    typeof ReadableStream !== 'undefined' && source instanceof ReadableStream
  );
}

function metadataKey(key: string): string {
  return `${encodeKey(key)}${METADATA_SUFFIX}`;
}

function isValidRange(range: OPFSBlobCacheRange): boolean {
  return (
    Number.isSafeInteger(range.start) &&
    Number.isSafeInteger(range.end) &&
    range.start >= 0 &&
    range.end >= range.start
  );
}

function mergeRanges(ranges: OPFSBlobCacheRange[]): OPFSBlobCacheRange[] {
  const sorted = ranges
    .filter(isValidRange)
    .sort((a, b) => a.start - b.start || a.end - b.end);
  const merged: OPFSBlobCacheRange[] = [];
  for (const range of sorted) {
    const last = merged.at(-1);
    if (!last || range.start > last.end + 1) {
      merged.push({ ...range });
      continue;
    }
    last.end = Math.max(last.end, range.end);
  }
  return merged;
}

function completeRangesForSize(size: number): OPFSBlobCacheRange[] {
  return size > 0 ? [{ start: 0, end: size - 1 }] : [];
}

function rangesCoverSize(ranges: OPFSBlobCacheRange[], size: number): boolean {
  if (size === 0) return true;
  const [first] = mergeRanges(ranges);
  return first?.start === 0 && first.end >= size - 1;
}

function highestRangeEnd(ranges: OPFSBlobCacheRange[] | undefined): number {
  if (!ranges?.length) return -1;
  return Math.max(...ranges.filter(isValidRange).map(range => range.end));
}

function isCacheTokenCompatible(
  metadata: OPFSBlobCacheInfo | null,
  cacheToken: string | undefined
): boolean {
  return !metadata || !cacheToken || metadata.cacheToken === cacheToken;
}

function isCompleteMetadataFor(
  metadata: OPFSBlobCacheInfo,
  options: OPFSBlobCacheCompleteOptions = {}
): boolean {
  const expectedSize = options.size ?? metadata.size;
  const tokenCompatible =
    isCacheTokenCompatible(metadata, options.cacheToken) ||
    (metadata.source === 'upload' &&
      !metadata.cacheToken &&
      metadata.size === expectedSize);
  return (
    Number.isSafeInteger(metadata.size) &&
    metadata.size >= 0 &&
    metadata.size === expectedSize &&
    metadata.complete === true &&
    Array.isArray(metadata.ranges) &&
    rangesCoverSize(metadata.ranges, expectedSize) &&
    tokenCompatible
  );
}

function withMetadataMime(file: File, metadata: OPFSBlobCacheInfo): File {
  if (file.type === metadata.mime) return file;

  return new File([file], file.name, {
    type: metadata.mime,
    lastModified: file.lastModified,
  });
}

function parseContentRange(value: string | null): {
  start: number;
  end: number;
  size: number;
} | null {
  if (!value) return null;
  const match = /^bytes\s+(\d+)-(\d+)\/(\d+)$/i.exec(value.trim());
  if (!match) return null;
  const start = Number(match[1]);
  const end = Number(match[2]);
  const size = Number(match[3]);
  if (
    !Number.isSafeInteger(start) ||
    !Number.isSafeInteger(end) ||
    !Number.isSafeInteger(size) ||
    start < 0 ||
    end < start ||
    size < 0
  ) {
    return null;
  }
  return { start, end, size };
}

function getMissingRanges(
  ranges: OPFSBlobCacheRange[],
  size: number,
  chunkSize: number
): OPFSBlobCacheRange[] {
  if (size <= 0) return [];
  const normalized = mergeRanges(ranges);
  const missing: OPFSBlobCacheRange[] = [];
  let cursor = 0;
  for (const range of normalized) {
    if (range.start > cursor) {
      appendChunkedRanges(missing, cursor, range.start - 1, chunkSize);
    }
    cursor = Math.max(cursor, range.end + 1);
    if (cursor >= size) break;
  }
  if (cursor < size) {
    appendChunkedRanges(missing, cursor, size - 1, chunkSize);
  }
  return missing;
}

function appendChunkedRanges(
  target: OPFSBlobCacheRange[],
  start: number,
  end: number,
  chunkSize: number
): void {
  let cursor = start;
  while (cursor <= end) {
    const chunkEnd = Math.min(end, cursor + chunkSize - 1);
    target.push({ start: cursor, end: chunkEnd });
    cursor = chunkEnd + 1;
  }
}

async function writeTextFile(
  dir: FileSystemDirectoryHandle,
  name: string,
  text: string
): Promise<void> {
  const handle = await dir.getFileHandle(name, { create: true });
  const writable = await handle.createWritable();
  try {
    await writable.write(text);
    await writable.close();
  } catch (error) {
    await writable.abort().catch(() => {});
    throw error;
  }
}

async function readMetadata(
  dir: FileSystemDirectoryHandle,
  key: string
): Promise<OPFSBlobCacheInfo | null> {
  try {
    const handle = await dir.getFileHandle(metadataKey(key));
    const file = await handle.getFile();
    const data = JSON.parse(await file.text()) as OPFSBlobCacheInfo;
    return data && data.key === key ? data : null;
  } catch {
    return null;
  }
}

async function readRawFile(
  workspaceId: string,
  key: string
): Promise<File | null> {
  try {
    const dir = await getWorkspaceDir(workspaceId);
    const handle = await dir.getFileHandle(encodeKey(key));
    return await handle.getFile();
  } catch {
    return null;
  }
}

export const opfsBlobCache = {
  get available(): boolean {
    return opfsAvailable;
  },

  getAvailability(): OPFSBlobCacheAvailability {
    return opfsAvailable ? { available: true } : opfsAvailability;
  },

  getDiagnostics(): OPFSBlobCacheDiagnostics {
    return { ...diagnostics };
  },

  resetDiagnostics(): void {
    for (const key of Object.keys(
      diagnostics
    ) as (keyof OPFSBlobCacheDiagnostics)[]) {
      diagnostics[key] = 0;
    }
  },

  async has(workspaceId: string, key: string): Promise<boolean> {
    return (await this.getCompleteFile(workspaceId, key)) !== null;
  },

  async get(workspaceId: string, key: string): Promise<File | null> {
    return this.getFile(workspaceId, key);
  },

  async getFile(workspaceId: string, key: string): Promise<File | null> {
    return this.getCompleteFile(workspaceId, key);
  },

  async getCompleteFile(
    workspaceId: string,
    key: string,
    options: OPFSBlobCacheCompleteOptions = {}
  ): Promise<File | null> {
    if (!this.available) return null;
    try {
      const metadata = await this.getInfo(workspaceId, key);
      if (!metadata || !isCompleteMetadataFor(metadata, options)) {
        bump('misses');
        return null;
      }

      const file = await readRawFile(workspaceId, key);
      if (!file) {
        bump('misses');
        return null;
      }
      if (file.size !== metadata.size) {
        bump('misses');
        return null;
      }
      if (options.cacheToken && metadata.cacheToken !== options.cacheToken) {
        const dir = await getWorkspaceDir(workspaceId);
        await writeTextFile(
          dir,
          metadataKey(key),
          JSON.stringify({ ...metadata, cacheToken: options.cacheToken })
        );
      }

      bump('hits');
      return withMetadataMime(file, metadata);
    } catch {
      bump('misses');
      return null;
    }
  },

  async getInfo(
    workspaceId: string,
    key: string
  ): Promise<OPFSBlobCacheInfo | null> {
    if (!this.available) return null;
    try {
      const dir = await getWorkspaceDir(workspaceId);
      const metadata = await readMetadata(dir, key);
      return metadata;
    } catch {
      return null;
    }
  },

  async setUploadState(
    workspaceId: string,
    key: string,
    uploadState: OPFSBlobUploadState
  ): Promise<boolean> {
    if (!this.available) return false;
    try {
      const dir = await getWorkspaceDir(workspaceId);
      const metadata = await readMetadata(dir, key);
      if (!metadata?.complete) {
        return false;
      }
      await writeTextFile(
        dir,
        metadataKey(key),
        JSON.stringify({ ...metadata, uploadState })
      );
      return true;
    } catch {
      bump('errors');
      return false;
    }
  },

  async setFromBlob(
    workspaceId: string,
    key: string,
    source: File | Blob,
    options: OPFSBlobCacheSetOptions = {}
  ): Promise<boolean> {
    return await this.setFromStream(workspaceId, key, source.stream(), {
      mime: options.mime ?? source.type,
      size: options.size ?? source.size,
      source: options.source,
      cacheToken: options.cacheToken,
      uploadState: options.uploadState,
    });
  },

  /**
   * Write bytes to OPFS using streaming (no full file in RAM).
   * Metadata is committed only after the byte stream closes successfully.
   */
  async setFromStream(
    workspaceId: string,
    key: string,
    source: ReadableStream<Uint8Array>,
    options: OPFSBlobCacheSetOptions = {}
  ): Promise<boolean> {
    if (!this.available) return false;
    let writable: FileSystemWritableFileStream | null = null;
    let dir: FileSystemDirectoryHandle | null = null;

    try {
      dir = await getWorkspaceDir(workspaceId);
      const handle = await dir.getFileHandle(encodeKey(key), { create: true });
      writable = await handle.createWritable();
      await source.pipeTo(writable);
      writable = null;
      const file = await handle.getFile();
      if (options.size !== undefined && file.size !== options.size) {
        await dir.removeEntry(encodeKey(key)).catch(() => {});
        throw new Error(
          `OPFS blob ${key} wrote ${file.size} bytes, expected ${options.size}`
        );
      }
      const metadata: OPFSBlobCacheInfo = {
        key,
        mime: options.mime || file.type || 'application/octet-stream',
        size: options.size ?? file.size,
        createdAt: new Date().toISOString(),
        source: options.source ?? 'download',
        complete: true,
        ranges: completeRangesForSize(options.size ?? file.size),
        cacheToken: options.cacheToken,
        uploadState: options.uploadState,
      };
      await writeTextFile(dir, metadataKey(key), JSON.stringify(metadata));
      bump('fullWrites');
      return true;
    } catch {
      // Clean up partial writes
      bump('errors');
      await writable?.abort().catch(() => {});
      await dir?.removeEntry?.(encodeKey(key)).catch(() => {});
      await dir?.removeEntry?.(metadataKey(key)).catch(() => {});
      return false;
    }
  },

  async setRangeFromStream(
    workspaceId: string,
    key: string,
    start: number,
    source: ReadableStream<Uint8Array>,
    options: OPFSBlobCacheRangeOptions = {}
  ): Promise<void> {
    if (!this.available) return;
    if (!Number.isSafeInteger(start) || start < 0) {
      throw new Error(`Invalid OPFS range start: ${start}`);
    }
    if (
      options.expectedLength !== undefined &&
      (!Number.isSafeInteger(options.expectedLength) ||
        options.expectedLength < 0)
    ) {
      throw new Error(`Invalid OPFS range length: ${options.expectedLength}`);
    }

    let writable: FileSystemWritableFileStream | null = null;
    let bytesWritten = 0;
    let dir: FileSystemDirectoryHandle | null = null;

    try {
      dir = await getWorkspaceDir(workspaceId);
      const previous = await readMetadata(dir, key);
      if (!isCacheTokenCompatible(previous, options.cacheToken)) {
        throw new Error(`OPFS blob cache token mismatch for ${key}`);
      }
      const handle = await dir.getFileHandle(encodeKey(key), { create: true });
      writable = await handle.createWritable({ keepExistingData: true });
      await writable.seek(start);

      const reader = source.getReader();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const nextBytesWritten = bytesWritten + value.byteLength;
        if (
          options.expectedLength !== undefined &&
          nextBytesWritten > options.expectedLength
        ) {
          await reader.cancel().catch(() => {});
          throw new Error(
            `OPFS blob range ${key}@${start} exceeded expected length ${options.expectedLength}`
          );
        }
        bytesWritten = nextBytesWritten;
        await writable.write(new Uint8Array(value));
      }
      if (
        options.expectedLength !== undefined &&
        bytesWritten !== options.expectedLength
      ) {
        throw new Error(
          `OPFS blob range ${key}@${start} wrote ${bytesWritten} bytes, expected ${options.expectedLength}`
        );
      }
      await writable.close();
      writable = null;

      const file = await handle.getFile();
      const size =
        options.totalSize ?? options.size ?? previous?.size ?? file.size;
      const ranges = mergeRanges([
        ...(previous?.ranges ?? []),
        ...(bytesWritten > 0 ? [{ start, end: start + bytesWritten - 1 }] : []),
      ]);
      const metadata: OPFSBlobCacheInfo = {
        key,
        mime:
          options.mime ||
          previous?.mime ||
          file.type ||
          'application/octet-stream',
        size,
        createdAt: previous?.createdAt ?? new Date().toISOString(),
        source: options.source ?? 'range',
        complete: rangesCoverSize(ranges, size),
        ranges,
        cacheToken: options.cacheToken ?? previous?.cacheToken,
        uploadState: options.uploadState ?? previous?.uploadState,
      };
      await writeTextFile(dir, metadataKey(key), JSON.stringify(metadata));
      bump('rangeWrites');
      bump('rangeBytes', bytesWritten);
    } catch {
      bump('errors');
      await writable?.abort().catch(() => {});
      await dir?.removeEntry?.(encodeKey(key)).catch(() => {});
      await dir?.removeEntry?.(metadataKey(key)).catch(() => {});
      throw new Error(`Failed to write OPFS blob range ${key}@${start}`);
    }
  },

  async ensureCompleteFromRemote(
    workspaceId: string,
    key: string,
    options: OPFSBlobCacheRemoteOptions
  ): Promise<File | null> {
    if (!this.available) return null;

    try {
      const dir = await getWorkspaceDir(workspaceId);
      const existing = await this.getInfo(workspaceId, key);
      const existingCompleteFile = await this.getCompleteFile(
        workspaceId,
        key,
        {
          size: options.size,
          cacheToken: options.cacheToken,
        }
      );
      if (existingCompleteFile) return existingCompleteFile;

      const compatibleExisting = isCacheTokenCompatible(
        existing,
        options.cacheToken
      )
        ? existing
        : null;
      if (existing && !compatibleExisting) {
        await this.delete(workspaceId, key);
      }
      let reusableExisting = compatibleExisting;
      if (reusableExisting) {
        try {
          const handle = await dir.getFileHandle(encodeKey(key));
          const file = await handle.getFile();
          const requiredSize = highestRangeEnd(reusableExisting.ranges) + 1;
          const expectedExistingSize = options.size ?? reusableExisting.size;
          if (file.size < requiredSize || file.size > expectedExistingSize) {
            await this.delete(workspaceId, key);
            reusableExisting = null;
          }
        } catch {
          await this.delete(workspaceId, key);
          reusableExisting = null;
        }
      }

      const expectedSize = options.size ?? reusableExisting?.size ?? 0;
      if (expectedSize === 0) {
        const cached = await this.setFromBlob(
          workspaceId,
          key,
          new Blob([], { type: options.mime }),
          {
            mime: options.mime,
            size: 0,
            source: 'download',
            cacheToken: options.cacheToken,
          }
        );
        if (!cached) return null;
        bump('remoteCompletions');
        return await this.getCompleteFile(workspaceId, key, {
          size: 0,
          cacheToken: options.cacheToken,
        });
      }

      const chunkSize = Math.max(
        1,
        options.chunkSize ?? DEFAULT_RANGE_CHUNK_SIZE
      );
      const missing = getMissingRanges(
        reusableExisting?.ranges ?? [],
        expectedSize,
        chunkSize
      );

      for (const range of missing) {
        bump('rangeRequests');
        const response = await options.fetchRange(range.start, range.end);
        if (!response || !response.ok || !response.body) {
          return null;
        }

        // Older or proxy-stripped deployments may ignore Range and return the
        // complete object. Store it as a full stream and stop chunking.
        if (response.status === 200) {
          const contentLength = Number(
            response.headers.get('content-length') || 0
          );
          if (
            options.size !== undefined &&
            contentLength > 0 &&
            contentLength !== options.size
          ) {
            return null;
          }
          const cached = await this.setFromStream(
            workspaceId,
            key,
            response.body,
            {
              mime:
                options.mime ||
                response.headers.get('content-type') ||
                'application/octet-stream',
              size:
                options.size ??
                Number(response.headers.get('content-length') || 0),
              source: 'download',
              cacheToken: options.cacheToken,
            }
          );
          if (!cached) return null;
          bump('remoteCompletions');
          return await this.getCompleteFile(workspaceId, key, {
            size: options.size,
            cacheToken: options.cacheToken,
          });
        }

        if (response.status !== 206) {
          return null;
        }
        const contentRange = parseContentRange(
          response.headers.get('content-range')
        );
        if (
          !contentRange ||
          contentRange.start !== range.start ||
          contentRange.end !== range.end ||
          contentRange.size !== expectedSize
        ) {
          return null;
        }

        await this.setRangeFromStream(
          workspaceId,
          key,
          range.start,
          response.body,
          {
            mime:
              options.mime ||
              response.headers.get('content-type') ||
              'application/octet-stream',
            totalSize: options.size,
            expectedLength: range.end - range.start + 1,
            source: 'range',
            cacheToken: options.cacheToken,
          }
        );
      }

      const file = await this.getCompleteFile(workspaceId, key, {
        size: options.size,
        cacheToken: options.cacheToken,
      });
      if (file) bump('remoteCompletions');
      return file;
    } catch {
      bump('errors');
      return null;
    }
  },

  /**
   * Backwards-compatible write helper.
   */
  async set(
    workspaceId: string,
    key: string,
    source: File | Blob | ReadableStream<Uint8Array>,
    options: OPFSBlobCacheSetOptions = {}
  ): Promise<boolean> {
    if (isReadableByteStream(source)) {
      return await this.setFromStream(workspaceId, key, source, options);
    }
    return await this.setFromBlob(workspaceId, key, source, options);
  },

  async delete(workspaceId: string, key: string): Promise<void> {
    if (!this.available) return;
    try {
      const dir = await getWorkspaceDir(workspaceId);
      await dir.removeEntry(encodeKey(key)).catch(() => {});
      await dir.removeEntry(metadataKey(key)).catch(() => {});
    } catch {
      // Ignore if not found
    }
  },
};
